<template>
    <div>
        <input type="text"
               ref="input"
               :value="filledValue"
               class="form-control"
               @change="change"
               :class="{'is-invalid': isInvalid()}">
        <div class="invalid-feedback d-block" v-if="isInvalid()">
            <template v-if="filledValue === ''">{{ $t('form.errors.parameterNotFilled', [label]) }}</template>
            <template v-else-if="getValue() === null">{{ $t('form.errors.invalidDate') }}</template>
            <template v-else-if="isBeforeMinimum()">{{ $t('form.errors.dateBeforeMinimum', [label, formatBound(min)]) }}</template>
            <template v-else-if="isAfterMaximum()">{{ $t('form.errors.dateAfterMaximum', [label, formatBound(max)]) }}</template>
        </div>
    </div>
</template>

<script lang="ts">
import {DATE_FORMAT, i18n} from '@/components/DatePicker';
import {Control} from '@/composables/Control';
import moment from 'moment';
import Pikaday from 'pikaday';
import {defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

const MODEL_FORMAT = 'YYYY-MM-DD';

export default defineComponent({
    props: {
        value: {
            type: String as PropType<string | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        min: {
            type: String as PropType<string | null>,
            default: null,
        },
        max: {
            type: String as PropType<string | null>,
            default: null,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const input = ref<HTMLElement | null>(null);
        const filledValue = ref('');

        let pikaday: Pikaday;

        function getValue(): string | null {
            if (filledValue.value === '') {
                return null;
            }

            const date = moment(filledValue.value, DATE_FORMAT);

            return date.isValid()
                ? date.format(MODEL_FORMAT)
                : null;
        }

        function isBeforeMinimum(): boolean {
            const value = getValue();

            return value !== null && props.min !== null && value < props.min;
        }

        function isAfterMaximum(): boolean {
            const value = getValue();

            return value !== null && props.max !== null && value > props.max;
        }

        function formatBound(bound: string | null): string {
            return bound === null ? '' : moment(bound, MODEL_FORMAT).format(DATE_FORMAT);
        }

        function validateCurrentValue(): boolean {
            return filledValue.value === '' || (getValue() !== null && ! isBeforeMinimum() && ! isAfterMaximum());
        }

        const {updateValue, isInvalid} = Control<string>(props, emit, {
            getValue,
            validateCurrentValue,
        });

        // created logic
        if (props.value !== null) {
            filledValue.value = moment(props.value, MODEL_FORMAT).format(DATE_FORMAT);
            updateValue();
        }

        onMounted(() => {
            pikaday = new Pikaday({
                format: DATE_FORMAT,
                i18n,
                field: input.value as HTMLElement,
                minDate: props.min === null ? undefined : moment(props.min, MODEL_FORMAT).toDate(),
                maxDate: props.max === null ? undefined : moment(props.max, MODEL_FORMAT).toDate(),
            });
        });

        onBeforeUnmount(() => {
            pikaday.destroy();
        });

        function change(event: any): void {
            filledValue.value = event.target.value;
            updateValue();
        }

        return {
            input,
            filledValue,
            change,
            getValue,
            isInvalid,
            isBeforeMinimum,
            isAfterMaximum,
            formatBound,
        };
    },
});
</script>
